import React, { useEffect, useRef, useState } from 'react';
import { Result, Spin } from 'antd';
import Hls from 'hls.js';
import { LoadingOutlined } from '@ant-design/icons';
import { apiV2DistributionCabinetMonitorRefreshPost } from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

interface HlsVideoProps {
  src?: string;
  code?: string;
  height?: string | number;
  width?: string | number;
  minHeight?: string | number;
  objectFit?: React.CSSProperties['objectFit'];
  backgroundColor?: React.CSSProperties['backgroundColor'];
  onRecoverableNetworkError?: () => void;
}

const HlsVideo: React.FC<HlsVideoProps> = ({
  src,
  code,
  minHeight,
  height = 150,
  width,
  objectFit = 'cover',
  backgroundColor,
  onRecoverableNetworkError,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>();

  useEffect(() => {
    if (!code) return;

    const refreshMonitor = () => {
      apiV2DistributionCabinetMonitorRefreshPost({ list: [code] }).catch(() => {});
    };

    refreshMonitor();
    const timer = window.setInterval(refreshMonitor, 30000);
    return () => window.clearInterval(timer);
  }, [code]);

  useEffect(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!src || !videoRef.current) return;

    if (!src.includes('http') && !src.includes('https')) {
      setStatus('error');
      return;
    }
    setStatus('loading');

    if (Hls.isSupported()) {
      const hls = new Hls({
        autoStartLoad: true,
        startPosition: -1,
        liveSyncDuration: 3,
        liveMaxLatencyDuration: 10,
        enableWorker: true,
        lowLatencyMode: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 1024 * 1024 * 50,
        maxBufferHole: 2,
        fragLoadingMaxRetry: 5,
        fragLoadingRetryDelay: 800,
        abrBandWidthFactor: 0.8,
        abrBandWidthUpFactor: 0.7,
      });

      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setStatus('success');
        videoRef.current?.play().catch(() => {});
      });

      let retry = 0;
      const maxRetry = 5;
      hls.on(Hls.Events.ERROR, (_, data) => {
        const response = (data as any)?.response;
        const statusCode = response?.code ?? response?.status;
        if (
          data.type === Hls.ErrorTypes.NETWORK_ERROR &&
          [401, 404].includes(Number(statusCode)) &&
          onRecoverableNetworkError
        ) {
          onRecoverableNetworkError();
          hls.destroy();
          if (hlsRef.current === hls) {
            hlsRef.current = null;
          }
          return;
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (retry < maxRetry) {
                retry++;
                hls.startLoad();
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              setStatus('error');
              hls.destroy();
              break;
          }
        }
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = src;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [onRecoverableNetworkError, src, videoRef]);

  return (
    <div className={styles.videoStyle} style={{ height, width, minHeight, backgroundColor }}>
      {status === 'loading' && (
        <div className={styles.loading}>
          <Spin indicator={<LoadingOutlined spin />} tip="视频加载中..." size="large" />
        </div>
      )}
      {status === 'error' && (
        <div className={styles.error}>
          <Result status="error" icon="" title="视频加载失败" subTitle="请检查网络或视频源地址" />
        </div>
      )}
      <video
        ref={videoRef}
        controls={false}
        autoPlay
        muted
        preload="auto"
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit,
          backgroundColor,
          display: status === 'success' ? 'block' : 'none',
        }}
      >
        <source src={src} type="application/x-mpegURL" />
        <p>不支持的监控视频</p>
      </video>
    </div>
  );
};

export default HlsVideo;
